import { Box } from '@chakra-ui/react';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { IdleTimer, SpinnerModal } from 'shared-components';
import { ISessionContext, useSession } from 'shared-components/authentication';
import { studentBaseUrl } from './App';
import { useCustomNavigate } from './hooks/useCustomNavigate';
import { setNavigateStudentRecordAction } from './middleware/actions';
import CustomRoutes from './routes';
import { ApplicationState } from './store';
import { authSuccess as authActions_authSuccess, logout } from './store/auth/actions';
import { UserRole } from './store/auth/types';
import { setSagaContext } from './store/configureStore';
import { confirmSagaComplete } from './store/student/actions';
import {
  setContextLoading as uiActions_setContextLoading,
  setContextSuccess as uiActions_setContextSuccess,
} from './store/ui/actions';
import { hasPerm, P } from './utilities/roles/permissions';

function ContextWrapper() {
  const dispatch = useDispatch();

  const { navigateTo, navigateForward } = useCustomNavigate();

  const navigateStudentRecord = useSelector((state: ApplicationState) => state.ui.navigateStudentRecord);
  const userRole: UserRole = useSelector((state: ApplicationState) => state.auth.role);
  const offerId = useSelector((state: ApplicationState) => state.student.offer.offerId);
  const isContextLoading = useSelector((state: ApplicationState) => state.ui.isContextLoading ?? true);

  const location = useLocation();
  const isLoggedIn = useSelector((state: ApplicationState) => state.auth.isLoggedIn);

  const setContextLoading = useCallback(
    (isLoading: boolean) => dispatch(uiActions_setContextLoading(isLoading)),
    [dispatch],
  );
  const setContextSuccess = useCallback(
    sessionContext => dispatch(uiActions_setContextSuccess(sessionContext)),
    [dispatch],
  );
  const authSuccess = useCallback(
    (sessionContext: Omit<ISessionContext, 'getIdToken' | 'onLogout'>) =>
      dispatch(authActions_authSuccess(sessionContext)),
    [dispatch],
  );

  const session = useSession();
  const { isSessionValid } = session;

  const userHasPerm = hasPerm(userRole, P.CHECKLIST_NAVIGATE_FORWARD);

  useEffect(() => {
    const w = window as any;
    if (w.Appcues) {
      if (isLoggedIn && location.pathname === '/') {
        w.Appcues.page();
      }
    }
  }, [location, isLoggedIn]);

  useEffect(() => {
    setContextLoading(true);

    if (isSessionValid) {
      const sessionContext = { ...session };
      authSuccess(sessionContext);

      setSagaContext(session); // see configureStore
      setContextSuccess(session);
    } else {
      setContextLoading(false);
    }
  }, [isSessionValid]);

  useEffect(() => {
    if (!location.pathname.startsWith(studentBaseUrl) || !navigateStudentRecord || isContextLoading) return;
    if (userHasPerm) {
      navigateForward();
    } else {
      navigateTo(`${offerId}`);
    }
    dispatch(setNavigateStudentRecordAction(false));
    dispatch(confirmSagaComplete());
  }, [
    navigateStudentRecord,
    dispatch,
    offerId,
    userHasPerm,
    location.pathname,
    navigateForward,
    navigateTo,
    isContextLoading,
  ]);

  const onLogout = async () => {
    await session.onLogout(true);
    dispatch(logout());
  };

  // Vite specific env var's cannot be in shared-comps used by Nextjs
  const timeout = Number(import.meta?.env?.VITE_TIMEOUT ?? 55 * 60 * 1000);
  const promptBeforeIdle = Number(import.meta?.env?.VITE_PROMPT_BEFORE_IDLE ?? 2 * 60 * 1000);

  if (isContextLoading || !session?.isSessionValid) {
    return (
      <Box bgColor="chakra-body-bg" width="100vw" height="100vh" position="absolute">
        <SpinnerModal show={true} />
      </Box>
    );
  }

  return (
    <IdleTimer onLogout={onLogout} timeout={timeout} promptBeforeIdle={promptBeforeIdle}>
      <CustomRoutes />
    </IdleTimer>
  );
}

export { ContextWrapper };
